import { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, AlertCircle, Copy, Check, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";

/**
 * TwoFactorPanel — end-user (non-admin) 2FA setup surface.
 *
 * Reuses the same backend endpoints as the admin panel (`/auth/2fa/*`) but
 * with user-focused copy. Drops into ProfilePanel so any signed-in user can
 * self-serve TOTP activation from Google Authenticator / Authy / 1Password /
 * Bitwarden and pull down 8 one-time backup codes.
 */
export default function TwoFactorPanel() {
  const { t } = useT();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [disableCode, setDisableCode] = useState("");

  const load = () => {
    api.get("/me/2fa/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false }));
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tfaErrSetup"));
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code: code.trim() });
      setBackupCodes(data.codes);
      setSetup(null);
      setCode("");
      await checkAuth();
      load();
      toast.success(t("tfaEnabled"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tfaErrCode"));
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async () => {
    if (!window.confirm(t("tfaConfirmDisable"))) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      setDisableCode("");
      await checkAuth();
      load();
      toast.success(t("tfaDisabled"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tfaErrCode"));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async (c, i) => {
    try {
      await navigator.clipboard.writeText(c);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch { /* ignore */ }
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      toast.success(t("tfaCopiedAll"));
    } catch { /* ignore */ }
  };

  return (
    <div className="ed-card p-6" data-testid="profile-two-factor-panel">
      <p className="ed-eyebrow flex items-center gap-2">
        <ShieldCheck size={12} strokeWidth={1.5} /> {t("tfaEyebrow")}
      </p>
      <h3 className="font-serif text-xl mt-2">{t("tfaTitle")}</h3>
      <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
        {t("tfaBody")}
      </p>

      {status && (
        <div className="mt-4 pt-4 border-t border-neutral-200" data-testid="user-2fa-status">
          {status.enabled ? (
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-emerald-700 text-[#F9F8F6] flex items-center justify-center shrink-0">
                <ShieldCheck size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight">{t("tfaStatusOn")}</p>
                <p className="text-xs text-neutral-600 mt-1">
                  {t("tfaActivatedOn")} {status.activated_at ? new Date(status.activated_at).toLocaleDateString() : "?"} · {t("tfaCodesLeft").replace("{n}", String(status.backup_codes_left ?? 0))}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                <AlertCircle size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-[16rem]">
                <p className="font-serif text-lg leading-tight">{t("tfaStatusOff")}</p>
                <p className="text-xs text-neutral-600 mt-1">
                  {t("tfaOffHint")}
                </p>
              </div>
              <button
                onClick={startSetup}
                disabled={busy || !!setup}
                data-testid="user-2fa-start"
                className="ed-btn shrink-0 inline-flex items-center gap-2 disabled:opacity-50"
              >
                <KeyRound size={12} strokeWidth={1.75} /> {t("tfaEnableBtn")}
              </button>
            </div>
          )}
        </div>
      )}

      {setup && (
        <div className="mt-4 pt-4 border-t border-neutral-200" data-testid="user-2fa-setup">
          <p className="ed-eyebrow">{t("tfaStep1")}</p>
          <div className="mt-4 flex flex-wrap gap-6 items-start">
            <img
              src={setup.qr_data_url}
              alt="2FA QR"
              data-testid="user-2fa-qr"
              style={{ width: 180, height: 180, imageRendering: "pixelated" }}
              className="border border-neutral-200"
            />
            <div className="flex-1 min-w-[16rem]">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                {t("tfaManualLabel")}
              </p>
              <p className="mt-2 font-mono text-xs break-all bg-neutral-50 border border-neutral-200 p-3">
                {setup.secret}
              </p>
              <form onSubmit={confirmSetup} className="mt-5">
                <label className="block">
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1.5">
                    {t("tfaStep2")}
                  </span>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    data-testid="user-2fa-confirm-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full border border-neutral-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] focus:border-neutral-900 outline-none"
                    placeholder="000000"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="user-2fa-confirm"
                  className="mt-4 w-full ed-btn bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50"
                >
                  {busy ? t("tfaConfirming") : t("tfaConfirmBtn")}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="mt-4 pt-4 border-t border-amber-500" data-testid="user-2fa-backup-codes">
          <p className="ed-eyebrow text-amber-800">{t("tfaBackupEyebrow")}</p>
          <h4 className="font-serif text-lg mt-2">{t("tfaBackupTitle")}</h4>
          <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
            {t("tfaBackupBody")}
          </p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-sm">
            {backupCodes.map((c, i) => (
              <button
                key={i}
                onClick={() => copyCode(c, i)}
                data-testid={`user-2fa-code-${i}`}
                className="bg-neutral-50 border border-neutral-200 hover:border-neutral-900 px-3 py-2.5 inline-flex items-center justify-between gap-1.5 transition-colors"
              >
                <span>{c}</span>
                {copiedIdx === i ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.5} className="opacity-40" />}
              </button>
            ))}
          </div>
          <div className="mt-4 flex gap-3">
            <button
              onClick={copyAll}
              data-testid="user-2fa-copy-all"
              className="ed-btn ed-btn-outline inline-flex items-center gap-2"
            >
              <Copy size={12} strokeWidth={1.75} /> {t("tfaCopyAll")}
            </button>
            <button
              onClick={() => setBackupCodes(null)}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-2 hover:bg-neutral-100"
            >
              {t("tfaBackupHide")}
            </button>
          </div>
        </div>
      )}

      {status?.enabled && !setup && (
        <div className="mt-4 pt-4 border-t border-neutral-200" data-testid="user-2fa-disable-card">
          <p className="ed-eyebrow">{t("tfaDisableTitle")}</p>
          <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
            {t("tfaDisableHint")}
          </p>
          <div className="mt-3 flex flex-wrap gap-3 items-end">
            <input
              type="text"
              maxLength={6}
              inputMode="numeric"
              data-testid="user-2fa-disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
              className="border border-neutral-300 px-3 py-2 font-mono text-center tracking-[0.3em] focus:border-neutral-900 outline-none"
              placeholder="000000"
              style={{ width: "10rem" }}
            />
            <button
              onClick={disable2fa}
              disabled={busy || disableCode.length !== 6}
              data-testid="user-2fa-disable-btn"
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-red-600 text-red-700 hover:bg-red-50 px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <ShieldOff size={12} strokeWidth={1.75} /> {t("tfaDisableBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
